"use client";
import type React from "react";
import { useState } from "react";
import { MessageInput } from "@/components/ui/message-input";
import {
  FEATURE_FLAGS,
  useFeatureFlagWithDefault,
} from "@/hooks/useFeatureFlag";

interface ChatBarProps {
  onMessageSubmit: (message: string, files?: File[]) => void;
  isVisible: boolean;
}

export function ChatBar({ onMessageSubmit, isVisible }: ChatBarProps) {
  const { isEnabled } = useFeatureFlagWithDefault(
    FEATURE_FLAGS.ENABLE_MIGRATION_AGENT,
    true
  );

  // If feature is disabled, render nothing.
  if (!isEnabled) return null;

  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[] | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() || (files && files.length > 0)) {
      onMessageSubmit(message, files || undefined);
      setMessage("");
      setFiles(null);
    }
  };

  return (
    <div
      className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 transition-opacity duration-400 ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Attachments displayed above bubble so bubble height stays constant */}
      {files && files.length > 0 && (
        <div className="flex gap-2 overflow-x-auto mb-2 justify-center">
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-1 text-sm text-gray-600 bg-gray-50 rounded-md px-2 py-1"
            >
              <span className="truncate max-w-[150px]">{file.name}</span>
              <button
                type="button"
                onClick={() => {
                  setFiles(files.filter((_, i) => i !== index));
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-full shadow-2xl border border-gray-200 px-6 w-[90vw] sm:min-w-[400px] sm:max-w-[600px] lg:max-w-[600px]">
        {/* Input field */}
        <form onSubmit={handleSubmit} className="flex items-center gap-3">
          <MessageInput
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ask me anything..."
            isGenerating={false}
            allowAttachments={true}
            files={files}
            setFiles={setFiles}
            className="min-h-[28px] pt-5 pb-0 px-2 border-0 bg-transparent shadow-none text-sm placeholder:text-gray-500 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none focus:ring-0"
          />
        </form>
      </div>
      {/* Bottom indicator line */}
      <div className="flex justify-center mt-2">
        <div className="w-12 h-1 bg-gray-400 rounded-full" />
      </div>
    </div>
  );
}
